/**
 * 要件定義pt 明細モーダル。
 *
 * 2 箇所から開く:
 *
 * - 当月pt 内訳カード（`MemberPtBreakdownCard`）の要件定義バー → 本人の 完了 / 処理中
 * - エンジニア看板の要件定義テーブルの行（`RequirementMemberDetailModal`）→ その人の
 *   完了 / 処理中 + **未対応（待処理）**
 *
 * バーや列は「要件定義に何pt か」までしか言わないので、「何の案件のどの成果物か」を
 * ここで開けるようにする。節は上から 完了 → 処理中 → 未対応 の工程順で、
 * **`openProjects` を渡したときだけ未対応節が出る**（当月pt 内訳カードは当月実績の内訳が
 * 主題なので未着手を混ぜない）。
 *
 * **どの節も母票（親チケット）単位に畳んで出す**。要件定義セットは 1 案件につき 5 工程の
 * 子票が自動生成されるため、子票を素で並べると同じ案件が 5 行繰り返され「何案件やって
 * いるのか」が読めない。完了 / 処理中はここで畳み（`groupRowsByParent`）、未対応は API が
 * 畳んだ結果をそのまま使う（`projectsToDetailGroups`）。
 *
 * 明細は呼出側が既に取得済みの行を受け取る（ここでは fetch しない）。pt の値と明細の
 * 母集団が同じ 1 回の取得から来ることが保証され、呼出側の数と明細の合計がずれない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義pt 明細モーダル（要件定義セット子票の完了分 + 処理中分 + 任意で未対応分）を母票単位に畳んで出す。母票・子票とも Backlog 票へのリンク付き
 * @graph-connects groupRowsByParent [calls] 完了 / 処理中の明細行を母票単位へ畳む
 * @graph-connects projectsToDetailGroups [calls] API が母票単位で返した未対応明細を同じ形へ写す
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { formatNum } from './combat-power/combat-power-utils.js';
import { IssueLink, SectionSpinner, TD_CLASS, TH_CLASS } from './pt-detail.js';
import type { MemberPtRow } from './member-pt-breakdown.js';
import {
  groupRowsByParent,
  projectsToDetailGroups,
  type RequirementDetailGroup,
} from './requirement-detail-groups.js';
import type { RequirementPendingProject } from '../../types/combat-power.js';

/** @graph-connects none */
interface RequirementPtDetailModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** クローズ要求コールバック */
  onClose: () => void;
  /** 対象月の表示ラベル（"YYYY/M"） */
  monthLabel: string;
  /** 要件定義セットの完了明細 */
  completedRows: readonly MemberPtRow[];
  /** 要件定義セットの処理中明細（現在スナップショット） */
  wipRows: readonly MemberPtRow[];
  /**
   * 要件定義セットの未対応（待処理）明細。渡したときだけ未対応節を出す。
   * 現在スナップショットで、pt は未着手のため予定pt。**API が母票単位に畳んだ結果をそのまま
   * 受け取る**（フロントで畳み直すと列の値と合計がずれ得るため）。
   */
  openProjects?: readonly RequirementPendingProject[];
  /** 見出しに出す対象者のニックネーム。未指定なら本人の明細として月だけを出す */
  nickname?: string;
  /** 明細の取得中か */
  isLoading: boolean;
}

/** @graph-connects none */
const SECTION_TITLE_CLASS = 'text-sm font-bold text-[var(--color-text-primary)]';

/** 母票が引けないグループの React key（母票キーが null のグループは 1 つだけ）。 @graph-connects none */
const UNKNOWN_PARENT_ROW_KEY = 'unknown-parent';

/** pt 合計（小数1位）。 @graph-connects none */
function totalOf(rows: readonly MemberPtRow[]): number {
  return Math.round(rows.reduce((acc, r) => acc + r.point, 0) * 10) / 10;
}

/**
 * 要件定義セット子票の明細テーブル（チケット / 件名 / 種別 / 完了日 / pt）。
 *
 * 共通の `PtDetailTable` ではなく専用テーブルにするのは、**種別列がこのモーダルの主役**で、
 * 完了節では完了日も要るため（共通テーブルは種別 + pt の 4 列で完了日を持たない）。
 * @graph-connects none
 */
function RequirementRowsTable({
  groups,
  showCompletedDate,
}: {
  groups: readonly RequirementDetailGroup[];
  /** 完了日列を出すか（完了節のみ。処理中は完了日を持たない） */
  showCompletedDate: boolean;
}) {
  const { t } = useTranslation();
  // 母票見出し行は全列ぶち抜き。列数は 完了日 の有無で変わる
  const columnCount = showCompletedDate ? 5 : 4;
  return (
    <div className="mt-2 overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colIssue')}</th>
            <th className={`${TH_CLASS} w-full`}>
              {t('members.quarterlyKpi.ptDetail.colSummary')}
            </th>
            <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colType')}</th>
            {showCompletedDate && (
              <th className={TH_CLASS}>{t('members.quarterlyKpi.ptDetail.colDate')}</th>
            )}
            <th className={`${TH_CLASS} text-right`}>pt</th>
          </tr>
        </thead>
        {groups.map((group) => (
          <tbody key={group.parentIssueKey ?? UNKNOWN_PARENT_ROW_KEY}>
            {/* 母票の見出し行。子票 5 工程が同じ案件の繰り返しになるので案件で畳む */}
            <tr className="bg-[var(--color-bg-elevated)]">
              <td
                colSpan={columnCount}
                className="px-2 py-1.5 text-xs font-medium text-[var(--color-text-secondary)]"
              >
                <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  {group.parentIssueKey === null ? (
                    <span>{t('members.ptBreakdown.detail.unknownParent')}</span>
                  ) : (
                    <IssueLink issueKey={group.parentIssueKey} />
                  )}
                  <span className="text-[var(--color-text-primary)]">
                    {group.parentSummary ?? '-'}
                  </span>
                  <span className="ml-auto whitespace-nowrap tabular-nums">
                    {t('members.ptBreakdown.detail.groupTotal', {
                      value: formatNum(group.point, 1),
                    })}
                  </span>
                </span>
              </td>
            </tr>
            {group.rows.map((d) => (
              <tr
                key={d.issueKey}
                className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
              >
                <td className={TD_CLASS}>
                  <IssueLink issueKey={d.issueKey} />
                </td>
                <td className={TD_CLASS}>{d.summary}</td>
                <td
                  className={`${TD_CLASS} text-xs text-[var(--color-text-secondary)] whitespace-nowrap`}
                >
                  {d.issueTypeName ?? '-'}
                </td>
                {showCompletedDate && (
                  <td
                    className={`${TD_CLASS} text-xs text-[var(--color-text-secondary)] whitespace-nowrap`}
                  >
                    {d.completedDate ?? '-'}
                  </td>
                )}
                <td className={`${TD_CLASS} text-right font-medium whitespace-nowrap`}>
                  {formatNum(d.point, 1)}
                </td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}

/** @graph-connects none */
export function RequirementPtDetailModal({
  open,
  onClose,
  monthLabel,
  completedRows,
  wipRows,
  openProjects,
  nickname,
  isLoading,
}: RequirementPtDetailModalProps) {
  const { t } = useTranslation();
  const completedTotal = totalOf(completedRows);
  const wipTotal = totalOf(wipRows);
  const completedGroups = useMemo(() => groupRowsByParent(completedRows), [completedRows]);
  const wipGroups = useMemo(() => groupRowsByParent(wipRows), [wipRows]);
  const openGroups = useMemo(
    () => (openProjects === undefined ? null : projectsToDetailGroups(openProjects)),
    [openProjects],
  );
  const openTotal =
    openGroups === null ? null : Math.round(openGroups.reduce((a, g) => a + g.point, 0) * 10) / 10;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        nickname === undefined
          ? t('members.ptBreakdown.detail.title', { month: monthLabel })
          : t('members.ptBreakdown.detail.titleWithName', { name: nickname, month: monthLabel })
      }
      // 未対応を含むときは「処理済み / 待処理」の 2 つの数を見出しに出す（列と突き合わせるため）
      description={
        openTotal === null
          ? t('members.ptBreakdown.detail.description', {
              value: formatNum(completedTotal + wipTotal, 1),
            })
          : t('members.ptBreakdown.detail.descriptionWithOpen', {
              processed: formatNum(completedTotal + wipTotal, 1),
              pending: formatNum(openTotal, 1),
            })
      }
      size="full"
      className="animate-bubble-in md:max-w-7xl"
    >
      <div className="space-y-6">
        {/* 完了 セクション */}
        <section>
          <h4 className={SECTION_TITLE_CLASS}>
            {t('members.ptBreakdown.detail.completedSection', {
              value: formatNum(completedTotal, 1),
            })}
          </h4>
          {isLoading ? (
            <SectionSpinner />
          ) : completedGroups.length > 0 ? (
            <RequirementRowsTable groups={completedGroups} showCompletedDate />
          ) : (
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              {t('members.ptBreakdown.detail.empty')}
            </p>
          )}
        </section>

        {/* 処理中 セクション */}
        <section>
          <h4 className={SECTION_TITLE_CLASS}>
            {t('members.ptBreakdown.detail.wipSection', { value: formatNum(wipTotal, 1) })}
          </h4>
          {isLoading ? (
            <SectionSpinner />
          ) : wipGroups.length > 0 ? (
            <RequirementRowsTable groups={wipGroups} showCompletedDate={false} />
          ) : (
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              {t('members.ptBreakdown.detail.empty')}
            </p>
          )}
        </section>

        {/* 未対応（待処理）セクション。openProjects を渡されたときだけ出す */}
        {openGroups !== null && openTotal !== null && (
          <section>
            <h4 className={SECTION_TITLE_CLASS}>
              {t('members.ptBreakdown.detail.openSection', { value: formatNum(openTotal, 1) })}
            </h4>
            {isLoading ? (
              <SectionSpinner />
            ) : openGroups.length > 0 ? (
              <RequirementRowsTable groups={openGroups} showCompletedDate={false} />
            ) : (
              <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                {t('members.ptBreakdown.detail.empty')}
              </p>
            )}
          </section>
        )}

        <p className="text-xs text-[var(--color-text-muted)]">
          {openGroups === null
            ? t('members.ptBreakdown.detail.note')
            : t('members.ptBreakdown.detail.noteWithOpen')}
        </p>
      </div>
    </Modal>
  );
}
